/**
 * The page "What runs today" (15 Oct 2026): the hook over `GET /status/today`
 * (`getPlatformStatus`).
 *
 * One read that composes facts other routes already compute: the live areas and their
 * shared mailboxes, shadow mode (the F&O gates and the egress gate, read only), the
 * switches of what I2Q does on its own, the data jobs and the last mail import run, the
 * cases per day, "Done without a person", the model cost of the month against its alert
 * level, the I2Q version, the data health and the open business decisions. Every
 * signed-in person, scoped to the own areas (15 Oct 2026); the route is in
 * `EXECUTIVE_ONLY_ROUTES`.
 *
 * The first part of the query key is the request key of the usage vocabulary
 * (`platform-status`). The server keeps the answer 5 minutes.
 */

import { useQuery } from "@tanstack/react-query";

import { getPlatformStatus, type PlatformStatus } from "./generated";

export type {
  PlatformCost,
  PlatformDay,
  PlatformEntity,
  PlatformShadow,
  PlatformStatus,
  PlatformSwitch,
  PlatformVersion,
} from "./generated";

/** The query keys; the first part is the request key of the usage vocabulary. */
export const platformKeys = {
  status: ["platform-status"] as const,
};

/** The server keeps the status 5 minutes. */
const STATUS_STALE_MS = 5 * 60_000;

/** The platform status (managers, admins, executives). */
export function usePlatformStatus(options: { enabled: boolean }) {
  return useQuery({
    queryKey: platformKeys.status,
    queryFn: async (): Promise<PlatformStatus | null> => {
      const { data } = await getPlatformStatus({ throwOnError: true });
      return data ?? null;
    },
    enabled: options.enabled,
    retry: false,
    staleTime: STATUS_STALE_MS,
  });
}
